// filepath: apps/web/src/features/lists/hooks/useEntityList.ts
//
// FR-UI-LIST-1 AC-1 / AC-3 / AC-4 — TanStack Query hook for the list view.
//
// Issues GET /api/entity-records/{entityType}?filter=&sort=&cursor=&limit=
// against the FR-QRY-1 query API. The cache key composes
// (tenantSlug, entityType, filterHash, sortKey, cursor) so:
//   - cross-tenant cache hits are impossible (INV-1)
//   - filter / sort changes produce a fresh cache entry (keyset
//     pagination — AC-3 forward-only)
//   - cursor advances land in a new cache entry per page
//
// Security / integrity invariants:
//   - INV-1: tenantSlug is the 2nd segment of the cache key tuple.
//   - INV-2: the renderer consumes exactly the records the server
//     returned — the hook does NOT issue per-row GETs to "fill in"
//     stripped fields, and the response shape is treated as
//     authoritative.
//   - The hook emits an `ApiError` on non-2xx responses so the page
//     can classify into one of the 5 mandatory error states
//     (FR-UI-CORE-10).
//   - 200 + `records: []` is treated as a SUCCESSFUL empty result,
//     not an error — the FR-QRY-3 AC-1 no-CanRead path. The page
//     distinguishes this from "permission-denied" via a separate
//     endpoint signal (see useEntityDefinition); if no signal is
//     available, the page defaults to empty-state chrome (NOT the
//     permission-denied banner — that would leak entity-type
//     existence per the impact report §"FR-UI-CORE-10 5-states").

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@/definitions/types";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import type {
  EntityListFilter,
  EntityListPageEnvelope,
  EntityListSort,
  ListResponseWire,
  NormalisedRecord,
  EntityRecordWire,
} from "../types";

/**
 * Default page size. Mirrors the server default at
 * `internal/query/handlers.go:DefaultLimit`. The server may override
 * with `page_size` from the response envelope (FR-QRY-1 AC-2).
 */
const DEFAULT_PAGE_SIZE = 20;

/**
 * TanStack Query cache key factory for list queries.
 *
 * Shape: `["list", tenantSlug, entityType, filterHash, sortKey, cursor ?? ""]`
 *
 * The tenantSlug segment is the SECOND segment in every emitted key
 * (INV-1 / R-1). The filterHash / sortKey / cursor segments are
 * appended to ensure distinct cache entries per filter / sort /
 * pagination advance.
 */
export const entityListKeys = {
  all: ["list"] as const,
  /**
   * Single list-page cache key. Used by `useEntityList` directly;
   * exported so consumers can invalidate the list cache on
   * record-write events (defensive — list views do not subscribe to
   * write events today).
   */
  page: (
    tenantSlug: string,
    entityType: string,
    filter: EntityListFilter[],
    sort: EntityListSort[],
    cursor: string | null,
  ) =>
    [
      "list",
      tenantSlug,
      entityType,
      hashFilter(filter),
      serialiseSort(sort),
      cursor ?? "",
    ] as const,
};

/**
 * Stable JSON serialisation of a filter array. Order-sensitive
 * (filter entries are AND'd in order).
 *
 * The server parses the `filter` URL param as a URL-encoded JSON
 * array (`internal/query/handlers.go:96-103`); we mirror that shape
 * so the cache key matches the URL the hook will issue.
 */
function hashFilter(filter: EntityListFilter[]): string {
  if (filter.length === 0) return "";
  return JSON.stringify(filter);
}

/**
 * Stable sort serialisation: comma-separated `field:dir` pairs (the
 * server's `sort` URL param shape — `internal/query/handlers.go:105-119`).
 */
function serialiseSort(sort: EntityListSort[]): string {
  if (sort.length === 0) return "";
  return sort.map((s) => `${s.field}:${s.dir}`).join(",");
}

export interface UseEntityListOptions {
  /** Target entity type id. Required. */
  entityType: string;
  /** Active filter expressions (URL-derived). */
  filter?: EntityListFilter[];
  /** Active sort expressions (URL-derived). */
  sort?: EntityListSort[];
  /**
   * Keyset cursor for the next page. When `null` or `""`, the hook
   * fetches the first page. When set, the hook appends it as a
   * distinct cache key segment so each page lands in its own entry.
   */
  cursor?: string | null;
  /**
   * Optional page-size override. The server caps at FR-QRY-1 AC-2's
   * server-enforced maximum.
   */
  pageSize?: number;
  /** API base URL override. Defaults to `/api`. */
  apiBase?: string;
  /**
   * When false, the query is disabled (no fetch). Used when the
   * definition is still loading or the entityType is missing.
   */
  enabled?: boolean;
}

export interface UseEntityListResult {
  /** Normalised result envelope. */
  data: EntityListPageEnvelope | undefined;
  /** True while the initial fetch is in flight. */
  isLoading: boolean;
  /** True while any fetch (including background refetch) is in flight. */
  isFetching: boolean;
  /** Last error, classified as an `ApiError` on non-2xx responses. */
  error: ApiError | null;
  /** Force refetch the current page (FR-UI-CORE-10 retry). */
  refetch: () => Promise<unknown>;
}

/**
 * useEntityList — TanStack Query hook backing the list view.
 *
 * See file header for invariants and security boundaries.
 */
export function useEntityList(opts: UseEntityListOptions): UseEntityListResult {
  const {
    entityType,
    filter = [],
    sort = [],
    cursor = null,
    pageSize = DEFAULT_PAGE_SIZE,
    apiBase = "/api",
    enabled = true,
  } = opts;

  const { tenantSlug } = useTenant();

  const queryKey = entityListKeys.page(
    tenantSlug,
    entityType,
    filter,
    sort,
    cursor,
  );

  const query: UseQueryResult<EntityListPageEnvelope, ApiError> = useQuery<
    EntityListPageEnvelope,
    ApiError
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      params.set("page_size", String(pageSize));
      if (filter.length > 0) {
        // Server parses `filter` as URL-encoded JSON array of
        // {field, op, value} (internal/query/handlers.go:96-103).
        params.set("filter", JSON.stringify(filter));
      }
      if (sort.length > 0) {
        // Server parses `sort` as comma-separated field:dir pairs
        // (internal/query/handlers.go:105-119).
        params.set("sort", serialiseSort(sort));
      }
      if (cursor !== null && cursor !== "") {
        params.set("cursor", cursor);
      }
      const url = `${apiBase}/entity-records/${encodeURIComponent(
        entityType,
      )}?${params.toString()}`;

      let response: Response;
      try {
        response = await apiFetch(url, {
          init: {
            headers: { Accept: "application/json" },
            signal,
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
          retryAfter,
        );
      }

      const data = (await response.json()) as ListResponseWire;
      return normalisePage(data);
    },
    enabled: enabled && entityType.length > 0,
    // List data: 30s freshness (per ARCHITECTURE_WEB.md §4 entity
    // data staleTime — matches useReferencePicker).
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Normalise the wire envelope into the consumer's camelCase shape.
 * Mirrors `useReferencePicker.normaliseList` but with the full
 * `Fields` map (the picker only reads one display field; the list
 * view reads every cell the server returned).
 */
function normalisePage(wire: ListResponseWire): EntityListPageEnvelope {
  const records: NormalisedRecord[] = Array.isArray(wire.records)
    ? wire.records.map((r) => normaliseRecord(r))
    : [];
  const nextCursor =
    typeof wire.next_cursor === "string" ? wire.next_cursor : "";
  const pageSize =
    typeof wire.page_size === "number" && wire.page_size > 0
      ? wire.page_size
      : DEFAULT_PAGE_SIZE;
  return { records, nextCursor, pageSize };
}

function normaliseRecord(record: EntityRecordWire): NormalisedRecord {
  return {
    id: record.ID,
    fields: record.Fields ?? {},
  };
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

function parseRetryAfter(header: string | null): number | null {
  if (header === null) return null;
  const trimmed = header.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (Number.isFinite(n) && n >= 0) return n;
  // Retry-After may be an HTTP-date; treat non-numeric as unknown.
  return null;
}
